import { Trip } from '../../types/cms.types';
import { supabase } from '../../config/supabase';

export interface TripFilter {
    search?: string;
    status?: string;
    vehicle_type?: string;
    date_from?: string;
    date_to?: string;
}

export interface TripListResponse {
    data: Trip[];
    total: number;
    page: number;
    limit: number;
    totalPages: number;
}

export class TripService {
    private static instance: TripService;

    public static getInstance(): TripService {
        if (!TripService.instance) {
            TripService.instance = new TripService();
        }
        return TripService.instance;
    }

    /**
     * Lấy danh sách trips với phân trang và filter
     */
    async getTrips(
        page: number = 1,
        limit: number = 10,
        filters?: TripFilter
    ): Promise<TripListResponse> {
        try {
            // Query trips table with actual column names
            let query = supabase
                .from('trips')
                .select('*', { count: 'exact' });

            // Apply filters
            if (filters?.search) {
                query = query.or(`
                    pickup_address.ilike.%${filters.search}%,
                    dropoff_address.ilike.%${filters.search}%,
                    customer_name.ilike.%${filters.search}%,
                    driver_name.ilike.%${filters.search}%
                `);
            }

            if (filters?.status) {
                query = query.eq('status', filters.status);
            }

            if (filters?.vehicle_type) {
                query = query.eq('vehicle_type', filters.vehicle_type);
            }

            if (filters?.date_from) {
                query = query.gte('created_at', filters.date_from);
            }

            if (filters?.date_to) {
                query = query.lte('created_at', filters.date_to);
            }

            // Apply pagination
            const from = (page - 1) * limit;
            const to = from + limit - 1;

            query = query
                .order('created_at', { ascending: false })
                .range(from, to);

            const { data, error, count } = await query;

            if (error) {
                throw error;
            }

            // Transform data to match expected format
            const transformedData: Trip[] = (data || []).map(trip => ({
                ...trip,
                // Map customer data for compatibility
                customer: {
                    id: trip.user_id,
                    full_name: trip.customer_name || 'Chưa có thông tin',
                    phone_number: trip.customer_phone_number || 'Chưa có SĐT'
                },
                // Map driver data for compatibility
                driver: trip.driver_id ? {
                    id: trip.driver_id,
                    full_name: trip.driver_name || 'Chưa phân công',
                    phone_number: trip.driver_phone_number || ''
                } : undefined,
                // Convert price from string to number for display
                price_number: parseFloat(trip.price || '0')
            }));

            const totalPages = Math.ceil((count || 0) / limit);

            return {
                data: transformedData,
                total: count || 0,
                page,
                limit,
                totalPages
            };
        } catch (error) {
            console.error('Error fetching trips:', error);
            throw error;
        }
    }

    /**
     * Lấy thông tin chi tiết một trip
     */
    async getTripById(id: string): Promise<Trip | null> {
        try {
            const { data, error } = await supabase
                .from('trips')
                .select(`
                    *,
                    customer:users!trips_customer_id_fkey(id, full_name, phone_number, email),
                    driver:drivers!trips_driver_id_fkey(id, full_name, phone_number, email, license_plate)
                `)
                .eq('id', id)
                .single();

            if (error) {
                throw error;
            }

            return data;
        } catch (error) {
            console.error('Error fetching trip:', error);
            throw error;
        }
    }

    /**
     * Cập nhật trạng thái trip
     */
    async updateTripStatus(id: string, status: string): Promise<Trip> {
        try {
            const { data, error } = await supabase
                .from('trips')
                .update({ 
                    status,
                    updated_at: new Date().toISOString()
                })
                .eq('id', id)
                .select()
                .single();

            if (error) {
                throw error;
            }

            return data;
        } catch (error) {
            console.error('Error updating trip status:', error);
            throw error;
        }
    }

    /**
     * Xóa trip
     */
    async deleteTrip(id: string): Promise<void> {
        try {
            const { error } = await supabase
                .from('trips')
                .delete()
                .eq('id', id);

            if (error) {
                throw error;
            }
        } catch (error) {
            console.error('Error deleting trip:', error);
            throw error;
        }
    }

    /**
     * Lấy thống kê trips
     */
    async getTripStats(): Promise<{
        total: number;
        completed: number;
        in_progress: number;
        cancelled: number;
        today: number;
    }> {
        try {
            const today = new Date().toISOString().split('T')[0];

            const [totalResult, completedResult, inProgressResult, cancelledResult, todayResult] = await Promise.all([
                supabase.from('trips').select('id', { count: 'exact', head: true }),
                supabase.from('trips').select('id', { count: 'exact', head: true }).eq('status', 'completed'),
                supabase.from('trips').select('id', { count: 'exact', head: true }).in('status', ['accepted', 'going_pickup', 'in_progress']),
                supabase.from('trips').select('id', { count: 'exact', head: true }).in('status', ['cancelled_by_driver', 'cancelled_by_user']),
                supabase.from('trips').select('id', { count: 'exact', head: true }).gte('created_at', today)
            ]);

            return {
                total: totalResult.count || 0,
                completed: completedResult.count || 0,
                in_progress: inProgressResult.count || 0,
                cancelled: cancelledResult.count || 0,
                today: todayResult.count || 0
            };
        } catch (error) {
            console.error('Error fetching trip stats:', error);
            throw error;
        }
    }
}

export default TripService;
